import { useEffect, useMemo, useRef, useState } from 'react'
import { dateFormats, TimeOfDay } from 'constants/common'
import formatFNS from 'date-fns/format'
import styles from './index.module.scss'
import { slotAvailabilityEnumMapping } from 'app/Bookings/constants'
import classNames from 'classnames'
import SlotGridButton from 'common/SlotGridButton'

type SlotCardProps = {
  providerDetails: Provider
  dates: Date[]
  generatedSlots: DateSlot
  showSlotSelection?: boolean
  selectedSlot?: SelectedSlot
  onSlotClick?: (selectedSlot: SelectedSlot) => void
  userSelectedAvailType?: string
}

const SlotCard: React.FC<SlotCardProps> = ({
  providerDetails,
  dates,
  generatedSlots,
  selectedSlot = null,
  onSlotClick,
  showSlotSelection = true,
  userSelectedAvailType,
}) => {
  const [noSlotsToShow, setNoSlotsToShow] = useState<boolean>(false)
  const slotGridBodyDivRef = useRef<HTMLDivElement>(null)

  const onCardClick = (slot: ProviderSlot) => {
    !!onSlotClick &&
      onSlotClick({
        slot,
        provider: providerDetails,
      })
  }

  const getSlots = (date: Date) =>
    generatedSlots[formatFNS(date, dateFormats.YEAR_MONTH_DAY_FORMAT)]

  const noSlotsForTheDate = (date: Date) =>
    !(getSlots(date)?.filter((date) => !!date)?.length > 0)
  const { id: providerId } = providerDetails

  const slotSessionTypeMatchesTypeSelectedInCheckbox = (slot: ProviderSlot) => {
    const checkAvailability =
      userSelectedAvailType === undefined || // Both types
      slot?.availabilityType?.some(
        (availability) => availability === userSelectedAvailType
      )
    return checkAvailability
  }

  const slotCountForTheDateByUserSelectedAvailFilter = (
    slots: ProviderSlot[]
  ) => {
    return slots.reduce(
      (total: number, slot: ProviderSlot) =>
        slot?.start &&
        slot?.practiceId === providerDetails?.practice?.id &&
        slotSessionTypeMatchesTypeSelectedInCheckbox(slot)
          ? total + 1
          : total,
      0
    )
  }

  const availableSessionType =
    userSelectedAvailType === 'in_person' ? 'Video' : 'In Person'

  useEffect(() => {
    slotGridBodyDivRef.current?.firstChild === null
      ? setNoSlotsToShow(true)
      : setNoSlotsToShow(false)
  }, [userSelectedAvailType, slotGridBodyDivRef])

  const areSlotsAvailable = useMemo(
    () => dates.some((date) => !noSlotsForTheDate(date)),
    [dates]
  )

  if (!areSlotsAvailable) {
    return (
      <div className={styles.noSessionMessage}>
        Sorry, {providerDetails.firstName} doesn&apos;t have any available
        sessions at the moment.
      </div>
    )
  }

  return (
    <>
      <div ref={slotGridBodyDivRef}>
        {dates.map((date) => {
          const slots = getSlots(date)

          if (
            noSlotsForTheDate(date) ||
            slotCountForTheDateByUserSelectedAvailFilter(slots) === 0
          ) {
            return null
          }

          return (
            <div
              key={`${providerId}-${date.getDate()}-${date.getMonth()}`}
              className={styles.DaySlots}
            >
              <div className={`${styles.DaySlots_heading}`}>
                {formatFNS(
                  new Date(date),
                  dateFormats.FULL_WEEKDAY_MONTH_DAY_FORMAT
                )}
              </div>

              <div
                className={styles.DaySlots_slotContainer}
                data-testid={`${providerDetails.id}-mobile-slots`}
              >
                {slots?.map((slot) => {
                  if (
                    slot?.start &&
                    slot?.practiceId === providerDetails?.practice?.id &&
                    slotSessionTypeMatchesTypeSelectedInCheckbox(slot)
                  ) {
                    const isSlotSelected = selectedSlot?.slot?.id === slot?.id
                    const isEveningSlot = slot?.timeOfDay === TimeOfDay.EVENING
                    const isDaySlot = slot?.timeOfDay && !isEveningSlot
                    return (
                      <SlotGridButton
                        key={`slot-${providerId}-${slot?.id}`}
                        slotButtonClass={classNames(
                          styles.DaySlots_slotContainer_slotButton,
                          {
                            [styles.DaySlots_slotContainer_slotButton_selected]:
                              isSlotSelected && showSlotSelection,
                            [styles.DaySlots_slotContainer_slotButton_daySlot]: isDaySlot,
                            [styles.DaySlots_slotContainer_slotButton_eveningSlot]: isEveningSlot,
                          }
                        )}
                        slot={slot}
                        onSlotClick={() => onCardClick(slot)}
                        showSlotAvailabilityIcon
                        slotAvailabilities={
                          userSelectedAvailType !== undefined
                            ? [userSelectedAvailType]
                            : slot?.availabilityType?.map(
                                (availability) =>
                                  slotAvailabilityEnumMapping[availability]
                              )
                        }
                      />
                    )
                  }
                })}
              </div>
            </div>
          )
        })}
      </div>
      {noSlotsToShow ? (
        <div className={styles.noSessionMessage}>
          Sorry, {providerDetails.firstName} is only available for{' '}
          {availableSessionType} sessions at this time.
        </div>
      ) : null}
    </>
  )
}

export default SlotCard
